import type { ProductFlyer } from '@avrahami/contracts';
import { flyerFigures, flyerSaving, makerLine, type FlyerFigure } from '../layout';
import { bodyFont, clip, displayFont, drawText, fit, type Page } from '../kit';

/**
 * Words and pieces more than one template uses. Every word here is true of every store: a quote
 * on request, its own phone, a sale only when an old price stands above the price. Warranty,
 * delivery, service or payments are not the platform's to promise for a store (adr/0050).
 */
export const WORDS = {
  cta: 'לקבלת הצעת מחיר',
  whatsapp: 'דברו איתנו בוואטסאפ',
  sale: 'מבצע',
  salePrice: 'מחיר מבצע',
  price: 'מחיר',
  regular: 'מחיר רגיל',
  saving: 'חוסכים',
  instead: 'במקום',
  quote: 'הצעת מחיר אישית',
  noCommitment: 'ללא התחייבות',
  sku: 'מק״ט',
  made: 'תוצרת',
} as const;

/** The saving of a sale, null without one. */
export const savingOf = (flyer: ProductFlyer) =>
  flyer.price === null ? null : flyerSaving(flyer.price);

/** Who made it and where, as far as the store's page shows it; empty when it shows nothing. */
export function originLine(flyer: ProductFlyer): string {
  const { maker } = flyer;
  return [maker.country === null ? '' : `${WORDS.made} ${maker.country}`, makerLine(maker)]
    .filter((part) => part !== '')
    .join(' · ');
}

/** A short line that names the product's place: its maker, else its category, else its SKU. */
export function kickerOf(flyer: ProductFlyer): string {
  const origin = originLine(flyer);
  if (origin !== '') return origin;
  return flyer.category ?? `${WORDS.sku} ${flyer.sku}`;
}

/** The figures of the product's spec rows, then its strengths, up to `count`. */
export type Tile =
  | { kind: 'FIGURE'; figure: FlyerFigure }
  | { kind: 'BENEFIT'; icon: ProductFlyer['benefits'][number]['icon']; title: string };

export function tilesOf(flyer: ProductFlyer, count: number): Tile[] {
  const tiles: Tile[] = flyerFigures(flyer.specs).map((figure) => ({ kind: 'FIGURE', figure }));
  for (const benefit of flyer.benefits) {
    tiles.push({ kind: 'BENEFIT', icon: benefit.icon, title: benefit.title });
  }
  return tiles.slice(0, count);
}

/** A spec row that gives the product's outer size, for a caption. */
export function sizeRow(flyer: ProductFlyer): string | null {
  return flyer.specs.find((row) => /מידות|גודל/.test(row.label))?.value ?? null;
}

/**
 * The second part of a title: numbers in the heavy type, words in the light one, as a printed
 * title sets "דו־תאי **50×50**". Breaks into lines no wider than `maxWidth`; answers its height.
 */
export function drawMixedTitle(
  page: Page,
  text: string,
  options: {
    right: number;
    top: number;
    size: number;
    maxWidth: number;
    color: string;
    numberColor?: string;
    lightWeight?: number;
  },
): number {
  const { ctx } = page;
  const heavy = displayFont(page, options.size);
  const light = bodyFont(page, options.lightWeight ?? 400, options.size * 1.04);
  const isNumber = (word: string) => /\d/.test(word);
  const widthOf = (word: string) => {
    ctx.font = isNumber(word) ? heavy : light;
    return ctx.measureText(word).width;
  };
  ctx.font = light;
  const space = ctx.measureText(' ').width;
  const lines: string[][] = [[]];
  let used = 0;
  for (const word of text.split(/\s+/).filter((part) => part !== '')) {
    const width = widthOf(word);
    const current = lines[lines.length - 1] ?? [];
    if (current.length > 0 && used + space + width > options.maxWidth) {
      lines.push([word]);
      used = width;
    } else {
      current.push(word);
      used += (current.length > 1 ? space : 0) + width;
    }
  }
  const lineHeight = options.size * 1.05;
  lines.forEach((words, index) => {
    let x = options.right;
    const baseline = options.top + options.size * 0.92 + index * lineHeight;
    for (const word of words) {
      const number = isNumber(word);
      const width = drawText(ctx, word, x, baseline, {
        font: number ? heavy : light,
        color: number ? (options.numberColor ?? options.color) : options.color,
        direction: number ? 'ltr' : 'rtl',
      });
      x -= width + space;
    }
  });
  return lines.length * lineHeight;
}

/** The largest size (from `largest` down) at which a mixed title takes at most `maxLines`. */
export function mixedTitleSize(
  page: Page,
  text: string,
  options: { maxWidth: number; largest: number; smallest: number; maxLines: number },
): number {
  // The heavy type is the wider one: a size it fits at fits the mixed line too.
  return fit(page, text, 'display', {
    maxWidth: options.maxWidth,
    maxLines: options.maxLines,
    maxSize: options.largest,
    minSize: options.smallest,
    weight: 800,
  }).size;
}

/** `text` cut to `maxWidth` in the body type. */
export function clipBody(page: Page, text: string, weight: number, size: number, maxWidth: number) {
  return clip(page.ctx, text, bodyFont(page, weight, size), maxWidth);
}
